import React, { useState } from 'react';
import { Field, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import CommonButton, { BUTTON_STYLES } from '../../../components/common-button/CommonButton';
import CommonDropdown from '../../../components/common-dropdown/CommonDropdown';
import { Input } from '../../../components/common-inputs';
import WithError from '../../../components/with-error/WithError';
import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import useMarketOptions from '../../../hooks/useMarketOptions';
import useSurveyTemplateOptions from '../../../hooks/useSurveyTemplateOptions';
import useRespondentVolumeOptions from '../../../hooks/useRespondentVolumeOptions';
import useGenderOptions from '../../../hooks/useGenderOptions';
import AgeFields from './AgeFields';
import CommonTextarea from '../../../components/common-textarea/CommonTextarea';
import SupportModal from '../support-modal/SupportModal';
import CommonButtonLink from '../../../components/common-button-link/CommonButtonLink';

import scss from './ResponseForm.module.scss';

function ResponseFormInner({ onSubmit, onClickForHelp }) {
  const { t } = useTranslation('scout');

  const [isShowSupportModal, setIsShowSupportModal] = useState(false);

  const { submitting } = useFormState();

  const marketOptions = useMarketOptions();
  const respondentVolumeOptions = useRespondentVolumeOptions();
  const surveyTemplateOptions = useSurveyTemplateOptions();
  const genderOptions = useGenderOptions();

  const handleOpenSupportModal = () => {
    setIsShowSupportModal(true);
  };

  const handleCloseSupportModal = () => {
    setIsShowSupportModal(false);
  };

  return (
    <form className={scss.responseFormWrapper} onSubmit={onSubmit}>
      <div className={scss.fieldsRow}>
        <Field name='company'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.ROW} label={`${t('company')}:`} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input
                  size='sm'
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                  onFocus={input.onFocus}
                  onBlur={input.onBlur}
                  isError={meta.touched && meta.error}
                  disabled={submitting}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='email'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.ROW} label={`${t('yourEmail')}:`} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input
                  size='sm'
                  type='mail'
                  name={input.name}
                  value={input.value}
                  onChange={input.onChange}
                  onFocus={input.onFocus}
                  onBlur={input.onBlur}
                  isError={meta.touched && meta.error}
                  disabled={submitting}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='market'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.ROW} label={t('market')} isBold extraClassName={scss.fieldMedium}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <CommonDropdown
                  options={marketOptions}
                  value={input.value}
                  name={input.name}
                  onSelect={(newValue) => input.onChange(newValue)}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  isError={meta.touched && meta.error}
                  disabled={submitting}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='respondentVolume'>
          {({ meta, input }) => (
            <WithLabel
              variant={LABEL_STYLES.ROW}
              label={t('respondentVolume')}
              isBold
              extraClassName={scss.fieldMedium}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <CommonDropdown
                  options={respondentVolumeOptions}
                  value={input.value}
                  name={input.name}
                  onSelect={(newValue) => input.onChange(newValue)}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  isError={meta.touched && meta.error}
                  disabled={submitting}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='surveyTemplate'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.ROW} label={t('surveyTemplate')} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <CommonDropdown
                  options={surveyTemplateOptions}
                  value={input.value}
                  name={input.name}
                  onSelect={(newValue) => input.onChange(newValue)}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  isError={meta.touched && meta.error}
                  disabled={submitting}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>
      </div>

      <div className={scss.bottomBlock}>
        <span className={scss.requirementsLabel}>{t('specificRequirements')}</span>
        <div className={scss.fieldsRow}>
          <Field name='gender'>
            {({ meta, input }) => (
              <WithLabel variant={LABEL_STYLES.ROW} label={t('gender')} isBold extraClassName={scss.fieldMedium}>
                <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                  <CommonDropdown
                    options={genderOptions}
                    value={input.value}
                    name={input.name}
                    onSelect={(newValue) => input.onChange(newValue)}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                    isError={meta.touched && meta.error}
                    disabled={submitting}
                  />
                </WithError>
              </WithLabel>
            )}
          </Field>

          <AgeFields disabled={submitting} />

          <Field name='region'>
            {({ meta, input }) => (
              <WithLabel variant={LABEL_STYLES.ROW} label={`${t('regionsCities')}:`} isBold>
                <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                  <Input
                    size='sm'
                    type='mail'
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    onFocus={input.onFocus}
                    onBlur={input.onBlur}
                    isError={meta.touched && meta.error}
                    disabled={submitting}
                  />
                </WithError>
              </WithLabel>
            )}
          </Field>
        </div>

        <div className={scss.fieldsRow}>
          <Field name='otherRequirements'>
            {({ meta, input }) => (
              <WithLabel variant={LABEL_STYLES.ROW} label={`${t('otherRequirements')}:`} isBold>
                <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                  <CommonTextarea
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    rows='2'
                    resize='none'
                    isError={meta.touched && meta.error}
                    disabled={submitting}
                    enableVoiceInput
                  />
                </WithError>
              </WithLabel>
            )}
          </Field>
        </div>
      </div>

      <div className={scss.actions}>
        <CommonButtonLink disabled={submitting} onClick={handleOpenSupportModal} type='button'>
          {t('clickForHelp')}
        </CommonButtonLink>

        <CommonButton styleColor={BUTTON_STYLES.PRIMARY} maxWidth={120} type='submit' isLoading={submitting}>
          {t('send')}
        </CommonButton>
      </div>

      <SupportModal isOpen={isShowSupportModal} onClose={handleCloseSupportModal} onClickForHelp={onClickForHelp} />
    </form>
  );
}

export default ResponseFormInner;
